/**
 * Draft lineup structure for a specific league-event combination
 */
const createEmptyDraft = () => ({
  lineup: Array(5).fill(null),
  alternateSelections: [],
  lotn: null,
  alternateLOTN: null,
  projectedTotal: 0,
});

const initialState = {
  // Current active lineup (for backward compatibility)
  lineup: Array(5).fill(null),
  alternateSelections: [],
  lotn: null,
  alternateLOTN: null,
  projectedTotal: 0,

  // Draft lineups stored by "leagueId-eventId" key
  // Example: { "league123-event456": { lineup: [...], lotn: 1, ... } }
  drafts: {},

  // Track current context
  currentDraftKey: null,
};

function lineupReducer(state = initialState, action) {
  switch (action.type) {
    case "SET_LINEUP":
      return {
        ...state,
        lineup: action.payload,
      };
    case "SET_ALTERNATE_SELECTIONS":
      return {
        ...state,
        alternateSelections: action.payload,
      };
    case "SET_LOTN":
      return {
        ...state,
        lotn: action.payload,
      };
    case "SET_ALTERNATE_LOTN":
      return {
        ...state,
        alternateLOTN: action.payload,
      };
    case "SET_PROJECTED_TOTAL":
      return {
        ...state,
        projectedTotal: action.payload,
      };
    case "RESET_LINEUP":
      return {
        ...state,
        lineup: createEmptyDraft().lineup,
      };
    case "RESET_ALTERNATE_SELECTIONS":
      return {
        ...state,
        alternateSelections: createEmptyDraft().alternateSelections,
      };
    case "RESET_LOTN":
      return {
        ...state,
        lotn: createEmptyDraft().lotn,
      };
    case "RESET_ALTERNATE_LOTN":
      return {
        ...state,
        alternateLOTN: createEmptyDraft().alternateLOTN,
      };
    case "RESET_PROJECTED_TOTAL":
      return {
        ...state,
        projectedTotal: createEmptyDraft().projectedTotal,
      };

    // Draft management actions
    case "SAVE_DRAFT":
      const { draftKey, draftData } = action.payload;
      return {
        ...state,
        drafts: {
          ...state.drafts,
          [draftKey]: draftData,
        },
        currentDraftKey: draftKey,
      };

    case "LOAD_DRAFT":
      const loadKey = action.payload;
      const draft = state.drafts[loadKey] || createEmptyDraft();
      return {
        ...state,
        lineup: draft.lineup,
        alternateSelections: draft.alternateSelections,
        lotn: draft.lotn,
        alternateLOTN: draft.alternateLOTN,
        projectedTotal: draft.projectedTotal,
        currentDraftKey: loadKey,
      };

    case "CLEAR_DRAFT":
      const clearKey = action.payload;
      const { [clearKey]: removed, ...remainingDrafts } = state.drafts;
      return {
        ...state,
        drafts: remainingDrafts,
      };

    case "CLEAR_ALL_DRAFTS":
      return {
        ...state,
        drafts: {},
        currentDraftKey: null,
      };

    default:
      return state;
  }
}

// Action creators
export const setLineup = (lineup) => ({
  type: "SET_LINEUP",
  payload: lineup,
});

export const setAlternateSelections = (alternateSelections) => ({
  type: "SET_ALTERNATE_SELECTIONS",
  payload: alternateSelections,
});

export const setLotn = (lotn) => ({
  type: "SET_LOTN",
  payload: lotn,
});

export const setAlternateLotn = (alternateLOTN) => ({
  type: "SET_ALTERNATE_LOTN",
  payload: alternateLOTN,
});

export const setProjectedTotal = (total) => ({
  type: "SET_PROJECTED_TOTAL",
  payload: total,
});

export const resetLineup = () => ({
  type: "RESET_LINEUP",
});

export const resetAlternateSelections = () => ({
  type: "RESET_ALTERNATE_SELECTIONS",
});

export const resetLotn = () => ({
  type: "RESET_LOTN",
});

export const resetAlternateLotn = () => ({
  type: "RESET_ALTERNATE_LOTN",
});

export const resetProjectedTotal = () => ({
  type: "RESET_PROJECTED_TOTAL",
});

// Draft management action creators
export const saveDraft = (leagueId, eventId, draftData) => ({
  type: "SAVE_DRAFT",
  payload: {
    draftKey: `${leagueId}-${eventId}`,
    draftData,
  },
});

export const loadDraft = (leagueId, eventId) => ({
  type: "LOAD_DRAFT",
  payload: `${leagueId}-${eventId}`,
});

export const clearDraft = (leagueId, eventId) => ({
  type: "CLEAR_DRAFT",
  payload: `${leagueId}-${eventId}`,
});

export const clearAllDrafts = () => ({
  type: "CLEAR_ALL_DRAFTS",
});

export default lineupReducer;
